"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SMALL,
  INPUT_MONO,
  LABEL,
  SELECT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  type HalfLifeResultStrings,
  HalfLifeResults,
} from "@/components/tools/chem-reference/half-life-results";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import {
  type HalfLifeError,
  type HalfLifeSolve,
  type Known,
  PRESETS,
  solveHalfLife,
  TIME_UNITS,
  timeToSeconds,
} from "@/lib/tool-kit/chem-reference/half-life";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";

/** English strings. A translated content file overrides these through `ui`. */
export const HALF_LIFE_CALCULATOR_STRINGS = {
  solveLabel: "Solve for",
  solveRemaining: "Amount remaining (N)",
  solveInitial: "Initial amount (N₀)",
  solveTime: "Elapsed time (t)",
  solveHalfLife: "Half-life (T½)",
  initialLabel: "Initial amount (N₀)",
  remainingLabel: "Amount remaining (N)",
  timeLabel: "Elapsed time (t)",
  halfLifeLabel: "Half-life (T½)",
  unitLabel: "Unit for {quantity}",
  amountUnitLabel: "Amount unit (optional)",
  amountUnitHint:
    "Any consistent unit — g, Bq, atoms, mCi. Only the ratio N ÷ N₀ matters.",
  amountUnitPlaceholder: "g",
  digitsLabel: "Significant figures in the answer",
  numberHint:
    "Plain, decimal-comma or scientific input: 5730, 5,73 or 4.468e9.",
  presetLabel: "Set the half-life to",
  presetNote:
    "Rounded values; evaluated half-lives differ slightly between data sources.",
  solvedElsewhere: "{quantity} is calculated below.",
  results: "Results",
  enterPrompt: "Enter the three known values to solve for the fourth.",
  invalidNumber:
    "“{field}” is not a number. Use digits, a decimal point, or 4.5e9.",
  errors: {
    amount: "Amounts must be greater than zero.",
    remainingTooBig:
      "The amount remaining cannot be larger than the initial amount: decay only removes material.",
    time: "Elapsed time cannot be negative.",
    halfLife: "The half-life must be greater than zero.",
    noDecay:
      "These values do not give a half-life: the amount must fall over a time greater than zero.",
  },
  halfLivesElapsed: "Half-lives elapsed (k)",
  percentRemaining: "Percent remaining",
  decayConstant: "Decay constant (λ)",
  meanLife: "Mean lifetime (τ)",
  perUnit: "{value} per {unit}",
  working: "The working",
  workK: "k = t ÷ T½ = {time} ÷ {half} = {k}",
  workKFromAmounts: "k = log₂(N₀ ÷ N) = log₂({n0} ÷ {n}) = {k}",
  workRemaining: "N = N₀ × (½)^k = {n0} × {factor} = {n}",
  workInitial: "N₀ = N ÷ (½)^k = {n} ÷ {factor} = {n0}",
  workTime: "t = k × T½ = {k} × {half} = {time}",
  workHalfLife: "T½ = t ÷ k = {time} ÷ {k} = {half}",
  workLambda: "λ = ln 2 ÷ T½ = 0.693147 ÷ {half} = {lambda}",
  workTau: "τ = 1 ÷ λ = T½ ÷ ln 2 = {tau}",
  tableHeading: "Amount left after each half-life",
  columnHalfLives: "Half-lives (k)",
  columnFraction: "Fraction left",
  columnPercent: "Percent left",
  columnAmount: "Amount",
  columnTime: "Elapsed time",
  copy: "Copy result",
  copied: "Copied!",
  copyText: "{solved}; {k} half-lives elapsed; {percent}% remaining",
} as const;

type Strings = typeof HALF_LIFE_CALCULATOR_STRINGS;

const DIGIT_CHOICES = ["3", "4", "5", "6"] as const;

const SOLVE_OPTIONS: readonly HalfLifeSolve[] = [
  "remaining",
  "initial",
  "time",
  "halfLife",
];

const SOLVE_STRING = {
  remaining: "solveRemaining",
  initial: "solveInitial",
  time: "solveTime",
  halfLife: "solveHalfLife",
} as const;

/** Blank → null; unreadable → NaN so the widget can say which field is wrong. */
function readField(raw: string): number | null {
  if (raw.trim() === "") {
    return null;
  }
  return parseMeasuredNumber(raw) ?? Number.NaN;
}

function UnitSelect({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <select
      aria-label={label}
      className={`${SELECT} w-28 shrink-0`}
      id={id}
      onChange={(event) => onChange(event.target.value)}
      value={value}
    >
      {TIME_UNITS.map((unit) => (
        <option key={unit.id} value={unit.id}>
          {unit.symbol}
        </option>
      ))}
    </select>
  );
}

function TimeField({
  id,
  label,
  unitLabel,
  value,
  unit,
  hint,
  onValue,
  onUnit,
}: {
  id: string;
  label: string;
  unitLabel: string;
  value: string;
  unit: string;
  hint?: string;
  onValue: (value: string) => void;
  onUnit: (value: string) => void;
}) {
  return (
    <div className="space-y-2">
      <label className={LABEL} htmlFor={id}>
        {label}
      </label>
      <div className="flex gap-2">
        <input
          className={INPUT_MONO}
          id={id}
          inputMode="decimal"
          onChange={(event) => onValue(event.target.value)}
          value={value}
        />
        <UnitSelect
          id={`${id}-unit`}
          label={unitLabel}
          onChange={onUnit}
          value={unit}
        />
      </div>
      {hint ? <p className="text-muted-foreground text-xs">{hint}</p> : null}
    </div>
  );
}

function errorMessage(strings: Strings, error: HalfLifeError): string {
  return strings.errors[error];
}

export default function HalfLifeCalculatorWidget() {
  const strings = useToolUi(HALF_LIFE_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [solve, setSolve] = useState<HalfLifeSolve>("remaining");
  const [initial, setInitial] = useState("100");
  const [remaining, setRemaining] = useState("");
  const [time, setTime] = useState("11460");
  const [timeUnit, setTimeUnit] = useState("y");
  const [half, setHalf] = useState("5730");
  const [halfUnit, setHalfUnit] = useState("y");
  const [amountUnit, setAmountUnit] = useState("g");
  const [digits, setDigits] = useState<(typeof DIGIT_CHOICES)[number]>("4");

  const raw = {
    initial: readField(initial),
    remaining: readField(remaining),
    time: readField(time),
    half: readField(half),
  };
  const fieldNames = {
    initial: strings.initialLabel,
    remaining: strings.remainingLabel,
    time: strings.timeLabel,
    half: strings.halfLifeLabel,
  };
  const solvedKey = {
    remaining: "remaining",
    initial: "initial",
    time: "time",
    halfLife: "half",
  }[solve] as keyof typeof raw;
  const invalid = (Object.keys(raw) as (keyof typeof raw)[]).find(
    (key) => key !== solvedKey && Number.isNaN(raw[key])
  );
  const known: Known = {
    initial: solvedKey === "initial" ? null : raw.initial,
    remaining: solvedKey === "remaining" ? null : raw.remaining,
    timeSec:
      solvedKey === "time" || raw.time === null
        ? null
        : timeToSeconds(raw.time, timeUnit),
    halfLifeSec:
      solvedKey === "half" || raw.half === null
        ? null
        : timeToSeconds(raw.half, halfUnit),
  };
  const result = invalid ? null : solveHalfLife(solve, known);

  function field(key: keyof typeof raw) {
    return key === solvedKey ? (
      <p className="rounded-lg border border-border bg-muted/40 p-3 text-muted-foreground text-sm">
        {strings.solvedElsewhere.replace("{quantity}", fieldNames[key])}
      </p>
    ) : null;
  }

  return (
    <>
      <PillGroup
        label={strings.solveLabel}
        name="hl-solve"
        onChange={setSolve}
        options={SOLVE_OPTIONS.map((value) => ({
          value,
          label: strings[SOLVE_STRING[value]],
        }))}
        value={solve}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        {field("initial") ?? (
          <TextField
            hint={strings.numberHint}
            id="hl-initial"
            label={strings.initialLabel}
            numeric
            onChange={setInitial}
            value={initial}
          />
        )}
        {field("remaining") ?? (
          <TextField
            id="hl-remaining"
            label={strings.remainingLabel}
            numeric
            onChange={setRemaining}
            value={remaining}
          />
        )}
        {field("time") ?? (
          <TimeField
            id="hl-time"
            label={strings.timeLabel}
            onUnit={setTimeUnit}
            onValue={setTime}
            unit={timeUnit}
            unitLabel={strings.unitLabel.replace(
              "{quantity}",
              strings.timeLabel
            )}
            value={time}
          />
        )}
        {field("half") ?? (
          <TimeField
            id="hl-half"
            label={strings.halfLifeLabel}
            onUnit={setHalfUnit}
            onValue={setHalf}
            unit={halfUnit}
            unitLabel={strings.unitLabel.replace(
              "{quantity}",
              strings.halfLifeLabel
            )}
            value={half}
          />
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className={LABEL}>{strings.presetLabel}</span>
        {PRESETS.map((preset) => (
          <button
            className={BUTTON_SMALL}
            key={preset.id}
            onClick={() => {
              setHalf(preset.value);
              setHalfUnit(preset.unit);
              if (solve === "halfLife") {
                setSolve("remaining");
              }
            }}
            type="button"
          >
            {preset.label}
          </button>
        ))}
      </div>
      <p className="text-muted-foreground text-xs">{strings.presetNote}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          hint={strings.amountUnitHint}
          id="hl-amount-unit"
          label={strings.amountUnitLabel}
          onChange={(value) => setAmountUnit(value.slice(0, 12))}
          placeholder={strings.amountUnitPlaceholder}
          value={amountUnit}
        />
        <SelectField
          id="hl-digits"
          label={strings.digitsLabel}
          onChange={setDigits}
          options={DIGIT_CHOICES.map((value) => ({ value, label: value }))}
          value={digits}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {invalid ? (
          <ErrorNotice>
            {strings.invalidNumber.replace("{field}", fieldNames[invalid])}
          </ErrorNotice>
        ) : null}
        {!invalid && result === null ? (
          <Notice>{strings.enterPrompt}</Notice>
        ) : null}
        {result && "error" in result ? (
          <ErrorNotice>{errorMessage(strings, result.error)}</ErrorNotice>
        ) : null}
        {result && !("error" in result) ? (
          <HalfLifeResults
            display={{
              fmt,
              sig: Number(digits),
              amountUnit: amountUnit.trim(),
              timeUnit,
              halfUnit,
            }}
            result={result}
            solve={solve}
            strings={strings as HalfLifeResultStrings}
          />
        ) : null}
      </section>
    </>
  );
}
